import type { Subcategory } from './model'

export const NOT_APPLICABLE = 'n/a'

export const OTHER_CATEGORY: Subcategory = {
  label: 'other_non_billing_document',
  displayLabel: 'Other (Non-Billing document)',
  description: '',
  deprecated: false,
  supersededBy: null,
  metadata: {
    phase: {
      label: 'other',
      displayLabel: 'Other',
    },
    outcome: {
      label: 'non_billing_document',
      displayLabel: 'Non-Billing document',
    },
    reason: {
      label: NOT_APPLICABLE,
      displayLabel: NOT_APPLICABLE,
    },
  },
}

export const EOB_CLASSIFICATIONS = [
  'explanation_of_benefits_financial',
  'other_billing_acknowledged_eob',
  'other_billing_acknowledged_eob_financial',
  'claim_acknowledged_eob_financial',
  'claim_acknowledged_eob_non_financial',
] as const

export const FINISH_CLASSIFICATION_CATEGORIES = ['other_non_billing_document']

export const FINISH_CLASSIFICATION_CATEGORIES_LEGACY = [
  'other_non_billing_document',
  ...EOB_CLASSIFICATIONS,
]

export const EOB_NON_FINANCIAL_CLASSIFICATION = 'claim_acknowledged_eob_non_financial'

export function isNonFinancialEob(category: string | null | undefined): boolean {
  return category === EOB_NON_FINANCIAL_CLASSIFICATION
}

export const APPEAL_PAYER_REQUIRED_FORM_CATEGORY = 'appeal_payer_required_form'

export function isAppealPayerRequiredForm(category: string | null | undefined): boolean {
  return category === APPEAL_PAYER_REQUIRED_FORM_CATEGORY
}

export const CLAIM_APPEAL_ACKNOWLEDGEMENT_CATEGORY = 'appeal_acknowledged'

export function isClaimAppealAcknowledgement(category: string | null | undefined): boolean {
  return category === CLAIM_APPEAL_ACKNOWLEDGEMENT_CATEGORY
}

export const CLAIM_ACKNOWLEDGED_CATEGORY = 'claim_acknowledged'

export function isClaimAcknowledged(category: string | null | undefined): boolean {
  return category === CLAIM_ACKNOWLEDGED_CATEGORY
}

export const APPEAL_DENIED_UPHELD_AUTOMATED_CATEGORIES = [
  'appeal_denied_upheld_no_prior_authorization',
  'appeal_denied_upheld_experimental',
] as const

export function isAppealDeniedUpheldAutomated(category: string | null | undefined): boolean {
  return APPEAL_DENIED_UPHELD_AUTOMATED_CATEGORIES.includes(
    category as (typeof APPEAL_DENIED_UPHELD_AUTOMATED_CATEGORIES)[number],
  )
}

export function isClaimAppealRejected(
  metadata: {
    phase: { label: string }
    outcome: { label: string }
  } | null,
): boolean {
  return metadata?.phase.label === 'claim_appeal' && metadata?.outcome.label === 'rejected'
}

export function isClaimAppealDeniedUpheldMedicalNecessity(
  metadata: {
    phase: { label: string }
    outcome: { label: string }
    reason: { label: string }
  } | null,
): boolean {
  return (
    metadata?.phase.label === 'claim_appeal' &&
    metadata?.outcome.label === 'denied_upheld' &&
    metadata?.reason.label === 'medical_necessity'
  )
}

export function isClaimAppealDeniedUpheldNoPriorAuthorization(
  metadata: {
    phase: { label: string }
    outcome: { label: string }
    reason: { label: string }
  } | null,
): boolean {
  return (
    metadata?.phase.label === 'claim_appeal' &&
    metadata?.outcome.label === 'denied_upheld' &&
    metadata?.reason.label === 'no_prior_authorization'
  )
}

export function isClaimAppealOverturned(
  metadata: {
    phase: { label: string }
    outcome: { label: string }
  } | null,
): boolean {
  return metadata?.phase.label === 'claim_appeal' && metadata?.outcome.label === 'overturned'
}

export function isClaimRequestMedicalRecords(
  metadata: {
    phase: { label: string }
    outcome: { label: string }
    reason: { label: string }
  } | null,
): boolean {
  return (
    metadata?.phase.label === 'claim' &&
    metadata?.outcome.label === 'request' &&
    metadata?.reason.label === 'medical_records'
  )
}

export function isClaimAppealDeniedUpheldNonCovered(
  metadata: {
    phase: { label: string }
    outcome: { label: string }
    reason: { label: string }
  } | null,
): boolean {
  return (
    metadata?.phase.label === 'claim_appeal' &&
    metadata?.outcome.label === 'denied_upheld' &&
    metadata?.reason.label === 'non_covered'
  )
}

export function isClaimAppealRejectedTimely(
  metadata: {
    phase: { label: string }
    outcome: { label: string }
    reason: { label: string }
  } | null,
): boolean {
  return (
    metadata?.phase.label === 'claim_appeal' &&
    metadata?.outcome.label === 'rejected' &&
    metadata?.reason.label === 'timely'
  )
}

export const PA_AUTOMATED_CATEGORIES = [
  'prior_authorization_approved',
  'prior_authorization_no_authorization_required',
  'prior_authorization_denied_medical_necessity',
  'prior_authorization_denied_out_of_network',
  'prior_authorization_request_additional_info_needed',
] as const

export function isPaAutomatedCategory(category: string | null | undefined): boolean {
  return PA_AUTOMATED_CATEGORIES.includes(category as (typeof PA_AUTOMATED_CATEGORIES)[number])
}

export const APPEAL_DENIED_UPHELD_OUT_OF_NETWORK_CATEGORY = 'appeal_denied_upheld_out_of_network'

export function isAppealDeniedUpheldOutOfNetwork(category: string | null | undefined): boolean {
  return category === APPEAL_DENIED_UPHELD_OUT_OF_NETWORK_CATEGORY
}

export function isClaimDeniedExperimental(
  metadata: {
    phase: { label: string }
    outcome: { label: string }
    reason: { label: string }
  } | null,
): boolean {
  return (
    metadata?.phase.label === 'claim' &&
    metadata?.outcome.label === 'denied' &&
    metadata?.reason.label === 'experimental'
  )
}

export const METADATA_TRANSLATIONS: Record<string, string> = {
  eob: 'EOB',
  eob_financial: 'EOB Financial',
  eob_non_financial: 'EOB Non-Financial',
}
